import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { notification } from 'antd';

import RemoteAccessAuditUsersContext from '@totem/components/services/remoteAccess/v2/audit/RemoteAccessAuditUsersContext';
import { QRUserRole } from '@totem/components/services/remoteAccess/v2/users/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { AUTH0_USERS_ROLES_ENDPOINT } from '@totem/utilities/endpoints';

// This feature audits accounts that are candidates for removal — only ever
// STALE or NEVER LOGGED IN, never ACTIVE — so unlike the Users/No-Site-Access
// tabs this filter is fixed rather than a user-selectable Segmented control.
const AUDIT_STATUSES = ['STALE', 'NEVER LOGGED IN'];

type Props = {
  children?: ReactNode;
};

const RemoteAccessAuditUsersContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const organizationId = userProfile.organization?.id;
  const [loading, setLoading] = useState<boolean>(true);
  const [allRows, setAllRows] = useState<QRUserRole[]>([]);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [emailSearch, setEmailSearch] = useState<string>('');
  const [nameSearch, setNameSearch] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  useEffect(() => {
    setEmailSearch('');

    setNameSearch('');

    setSelectedUserIds([]);

    setRefreshData(true);
  }, [organizationId]);

  useEffect(() => {
    if (!refreshData) {
      return;
    }

    setRefreshData(false);

    setLoading(true);

    fetch(AUTH0_USERS_ROLES_ENDPOINT, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: QRUserRole[]) => {
        setAllRows(result ?? []);
      })
      .catch(() => {
        notification.error({
          message: 'Failed to load remote access audit users.',
        });
      })
      .finally(() => setLoading(false));
  }, [refreshData, getApiToken]);

  const onRefresh = useCallback(() => {
    setRefreshData(true);
  }, []);

  const rows = useMemo(() => {
    const emailTerm = emailSearch.trim().toLowerCase();
    const nameTerm = nameSearch.trim().toLowerCase();

    return allRows.filter((row) => {
      if (!AUDIT_STATUSES.includes(row.status)) {
        return false;
      }
      if (emailTerm && !row.user.email?.toLowerCase().includes(emailTerm)) {
        return false;
      }
      if (nameTerm && !row.user.name?.toLowerCase().includes(nameTerm)) {
        return false;
      }
      return true;
    });
  }, [allRows, emailSearch, nameSearch]);

  return (
    <RemoteAccessAuditUsersContext.Provider
      value={{
        loading,
        rows,
        emailSearch,
        setEmailSearch,
        nameSearch,
        setNameSearch,
        selectedUserIds,
        setSelectedUserIds,
        onRefresh,
      }}
    >
      {children}
    </RemoteAccessAuditUsersContext.Provider>
  );
};

export default RemoteAccessAuditUsersContainer;
